import { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Check, Copy, X } from "lucide-react"
import { toast } from "@/components/portal/Toast"

/**
 * Rückkopplung für den Telefonbot (17.09.2026).
 *
 * Paul nach der Wochenauswertung von Hand: „können wir das implementieren,
 * dass die Plattform das eigenständig macht für den Voice-Bot und dann genauso
 * wie für alles andere Verbesserungen vorschlägt?"
 *
 * Je Bot-Anruf schaut das System nach: Warnung, Rückruf des Büros, Antwort des
 * Kunden per SMS-Link. Anrufe mit Nacharbeit werden gedeutet (warum, vermeidbar,
 * auf welcher Ebene), einmal je Woche entstehen daraus Vorschläge.
 *
 * 🔑 Ein Vorschlag wird nie von selbst zum Ticket (Paul, 17.09.2026: „das
 * sollte vorher bei Verbesserungen erscheinen und dann sollte der User es
 * bestätigen"). „Als Ticket anlegen …" zeigt erst, was rausginge — Titel und
 * Text änderbar —, und legt es nur nach „Ja, Ticket anlegen" an. Nie eine Regel
 * im Bot-Prompt: eine Bot-Änderung braucht Code, dev und Testanruf.
 */

type Spur = { typ: string; bezeichnung: string; zeit?: string }
type Deutung = { grund: string; grund_text?: string; konfidenz: number; vermeidbar: boolean; begruendung: string; verbesserung?: string; quelle?: string }
type Fall = { id: number; tenant_name: string | null; started_at: string; headline: string | null; anrufer_name: string | null
              flag_reason: string | null; spuren: Spur[]; deutung: Deutung | null; stand: string }
type Beleg = { id: number; tenant_name: string | null; started_at: string; headline: string | null; anrufer_name: string | null; begruendung?: string | null }
type Vorschlag = { id: number; grund: string; grund_text: string; ebene_text: string; titel: string; befund: string | null; vorschlag_text: string
                   anzahl: number; status: string; erstellt_at: string; issue_url: string | null; issue_nummer: number | null; issue_repo: string | null
                   belege_detail: Beleg[]
                   wirkung: { vorher?: { quote: number | null; gesamt: number }; nachher?: { quote: number | null; gesamt: number }; besser?: boolean | null; verworfen_grund?: string | null } | null }
type Daten = {
  kennzahlen?: { anrufe: number; ohne_nacharbeit: number; nacharbeit: number; quote_ohne_nacharbeit: number | null; rueckrufe: number
                 rueckruf_versuche: number; rueckruf_minuten: number; korrektur_vor_rueckruf: number; gedeutet: number; tageslimit: number
                 je_grund: { grund: string; text: string; anzahl: number; vermeidbar: number }[] }
  vorschlaege?: Vorschlag[]
  verdichtungen?: { id: number; erstellt_at: string; zeitraum_tage: number; zusammenfassung: string; modell: string | null }[]
  faelle?: Fall[]
}
type Vorschau = { id: number; repo: string; titel: string; text: string }
type BacklogAntwort = { ok: boolean; hinweis?: string; repo?: string; titel?: string; text?: string; issue_url?: string; issue_nummer?: number }

const tag = (iso: string) => new Date(iso).toLocaleString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })

async function post(url: string, body?: unknown): Promise<BacklogAntwort> {
  const r = await fetch(url, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
                              body: body === undefined ? undefined : JSON.stringify(body) })
  const text = await r.text()
  if (!r.ok) throw new Error(`HTTP ${r.status}: ${text.slice(0, 160)}`)
  return text ? JSON.parse(text) : {}
}

export function VoiceRueckkopplungBlock({ tage, tenant }: { tage: number; tenant: string }) {
  const qc = useQueryClient()
  const [vonHand, setVonHand] = useState<BacklogAntwort | null>(null)
  const [vorschau, setVorschau] = useState<Vorschau | null>(null)
  const daten = useQuery<Daten>({
    queryKey: ["rueckkopplung-voice", tage, tenant],
    queryFn: async () => {
      const p = new URLSearchParams({ tage: String(tage) }); if (tenant) p.set("tenant", tenant)
      const r = await fetch(`/api/v1/rueckkopplung/voice?${p}`, { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json()
    },
  })
  const neuLaden = () => qc.invalidateQueries({ queryKey: ["rueckkopplung-voice"] })
  const vorschauLaden = useMutation({
    mutationFn: async (id: number): Promise<Vorschau> => {
      const r = await fetch(`/api/v1/rueckkopplung/voice/vorschlaege/${id}/ticket`, { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return { id, ...(await r.json()) }
    },
    onSuccess: (d) => { setVorschau(d); setVonHand(null) },
    onError: (e) => toast("Vorschau fehlgeschlagen: " + (e instanceof Error ? e.message : String(e)), "error"),
  })
  const backlog = useMutation({
    mutationFn: (v: Vorschau) => post(`/api/v1/rueckkopplung/voice/vorschlaege/${v.id}/backlog`, { bestaetigt: true, titel: v.titel, text: v.text }),
    onSuccess: (d) => {
      if (d.ok) { toast(`✓ Ticket #${d.issue_nummer} im Backlog angelegt`); setVonHand(null) }
      else { toast(d.hinweis || "Ticket konnte nicht angelegt werden", "error"); setVonHand(d) }
      setVorschau(null)
      neuLaden()
    },
    onError: (e) => toast("Backlog fehlgeschlagen: " + (e instanceof Error ? e.message : String(e)), "error"),
  })
  const verwerfen = useMutation({
    mutationFn: (id: number) => post(`/api/v1/rueckkopplung/voice/vorschlaege/${id}/verwerfen`, { grund: null }),
    onSuccess: () => { toast("Vorschlag verworfen"); neuLaden() },
    onError: (e) => toast("Verwerfen fehlgeschlagen: " + (e instanceof Error ? e.message : String(e)), "error"),
  })
  const verdichten = useMutation({
    mutationFn: () => post(`/api/v1/rueckkopplung/voice/verdichten`),
    onSuccess: () => { toast("Wochenbericht Telefonbot erstellt"); neuLaden() },
    onError: (e) => toast("Verdichten fehlgeschlagen: " + (e instanceof Error ? e.message : String(e)), "error"),
  })

  const k = daten.data?.kennzahlen
  const vorschlaege = daten.data?.vorschlaege || []
  const entwuerfe = vorschlaege.filter((v) => v.status === "entwurf")
  const imBacklog = vorschlaege.filter((v) => v.status === "backlog")
  const bericht = (daten.data?.verdichtungen || [])[0]
  const faelle = daten.data?.faelle || []

  return (
    <section className="mb-6" data-testid="voice-rueckkopplung">
      <div className="flex items-baseline gap-3 flex-wrap mb-2">
        <h2 className="text-[14px] font-semibold">Telefonbot — wo das Büro nacharbeiten musste</h2>
        <button type="button" className="pp-btn pp-btn-sm" disabled={verdichten.isPending} onClick={() => verdichten.mutate()}
                title="Ein Modellaufruf über die letzten vier Wochen. Läuft sonst automatisch einmal je Woche.">
          {verdichten.isPending ? "Verdichte…" : "Wochenbericht jetzt erstellen"}
        </button>
      </div>

      <div className="pp-card p-4 mb-3 text-[12.5px]">
        {!k || k.anrufe === 0 ? <span className="text-muted-foreground">Noch keine Bot-Anrufe in der Nachschau.</span> : (
          <>
            <div className="flex items-baseline gap-4 flex-wrap mb-2">
              <span><span className="text-[22px] font-semibold">{k.quote_ohne_nacharbeit ?? "—"} %</span> <span className="text-muted-foreground">der {k.anrufe} Bot-Anrufe ohne Nacharbeit</span></span>
              <span><span className="text-[22px] font-semibold">{k.rueckrufe}</span> <span className="text-muted-foreground">Rückrufe des Büros danach ({k.rueckruf_minuten} Min., dazu {k.rueckruf_versuche} Versuche ohne Gespräch)</span></span>
            </div>
            {k.korrektur_vor_rueckruf > 0 && (
              <div className="mb-2" style={{ color: "#b45309" }}>
                {k.korrektur_vor_rueckruf}× rief das Büro an, obwohl der Kunde per SMS-Link schon geantwortet hatte.
              </div>
            )}
            {k.je_grund.length === 0 ? <span className="text-muted-foreground">Noch keine Deutung — sie läuft einmal am Tag, sobald das Fenster von 72 Stunden nach dem Anruf zu ist.</span> : (
              <ul className="grid md:grid-cols-2 gap-x-4 gap-y-1">
                {k.je_grund.map((g) => <li key={g.grund}><strong>{g.anzahl}×</strong> {g.text}{g.vermeidbar ? ` · ${g.vermeidbar} vermeidbar` : ""}</li>)}
              </ul>
            )}
          </>
        )}
      </div>

      <h3 className="text-[13px] font-semibold mb-1.5">Vorschläge für den Bot ({entwuerfe.length})</h3>
      {entwuerfe.length === 0 ? (
        <div className="pp-card p-4 mb-3 text-[13px] text-muted-foreground">
          Keine offenen Vorschläge. Sie entstehen im Wochenbericht, sobald ein vermeidbarer Grund mindestens dreimal vorkam.
        </div>
      ) : (
        <div className="pp-card divide-y mb-3">
          {entwuerfe.map((v) => (
            <div key={v.id} className="px-4 py-3 text-[13px]" data-testid={`voice-vorschlag-${v.id}`}>
              <div className="font-medium">{v.titel}</div>
              <div className="text-[12.5px] text-muted-foreground mt-0.5">{v.grund_text} · {v.ebene_text} · {v.anzahl} Belege{v.befund ? ` — ${v.befund}` : ""}</div>
              <div className="mt-1.5" style={{ whiteSpace: "pre-wrap" }}>{v.vorschlag_text}</div>
              <details className="mt-1.5 text-[12.5px] text-muted-foreground">
                <summary className="cursor-pointer">Belege</summary>
                <ul className="mt-1 ml-4 list-disc">
                  {v.belege_detail.map((b) => (
                    <li key={b.id}>{tag(b.started_at)} · {b.tenant_name}{b.anrufer_name ? ` · ${b.anrufer_name}` : ""} — {b.headline || "Anruf"}{b.begruendung ? `: ${b.begruendung}` : ""}</li>
                  ))}
                </ul>
              </details>
              <div className="flex gap-2 mt-2">
                <button type="button" className="pp-btn pp-btn-sm" disabled={vorschauLaden.isPending || backlog.isPending}
                        onClick={() => vorschauLaden.mutate(v.id)} title="Zeigt erst, was als Ticket rausginge. Angelegt wird nichts, bevor Sie bestätigen.">
                  <Check className="w-3.5 h-3.5 inline mr-1" />Als Ticket anlegen …
                </button>
                <button type="button" className="pp-btn pp-btn-sm" disabled={verwerfen.isPending} onClick={() => verwerfen.mutate(v.id)}>
                  <X className="w-3.5 h-3.5 inline mr-1" />Verwerfen
                </button>
              </div>
              {vorschau?.id === v.id && (
                <div className="mt-3 border rounded p-3" data-testid="voice-ticket-bestaetigung">
                  <div className="text-[12.5px] mb-2">
                    Dieses Ticket würde im Backlog <strong>{vorschau.repo}</strong> angelegt. Titel und Text können Sie vorher ändern.
                  </div>
                  <input className="w-full border rounded px-2 py-1 mb-2 bg-transparent text-[13px] font-medium" aria-label="Titel des Tickets"
                         value={vorschau.titel} onChange={(e) => setVorschau({ ...vorschau, titel: e.target.value })} />
                  <textarea className="w-full border rounded px-2 py-1 mb-2 bg-transparent text-[12.5px] font-mono" rows={12} aria-label="Text des Tickets"
                            value={vorschau.text} onChange={(e) => setVorschau({ ...vorschau, text: e.target.value })} />
                  <div className="flex gap-2">
                    <button type="button" className="pp-btn pp-btn-sm" disabled={backlog.isPending || !vorschau.titel.trim()}
                            onClick={() => backlog.mutate(vorschau)}>
                      {backlog.isPending ? "Lege an…" : "Ja, Ticket anlegen"}
                    </button>
                    <button type="button" className="pp-btn pp-btn-sm" onClick={() => setVorschau(null)}>Abbrechen</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {vonHand && !vonHand.ok && vonHand.text && (
        <div className="pp-card p-4 mb-3 text-[12.5px]" data-testid="voice-ticket-von-hand">
          <div className="mb-1">{vonHand.hinweis} Ziel: <strong>{vonHand.repo}</strong></div>
          <div className="font-medium mb-1">{vonHand.titel}</div>
          <pre className="whitespace-pre-wrap text-[12px] border rounded p-2 mb-2">{vonHand.text}</pre>
          <button type="button" className="pp-btn pp-btn-sm"
                  onClick={() => { navigator.clipboard?.writeText(`${vonHand.titel}\n\n${vonHand.text}`); toast("Tickettext kopiert") }}>
            <Copy className="w-3.5 h-3.5 inline mr-1" />Text kopieren
          </button>
        </div>
      )}

      {bericht && (
        <div className="pp-card p-4 mb-3 text-[13px]">
          <div className="text-[12px] text-muted-foreground mb-1">
            Wochenbericht Telefonbot · {new Date(bericht.erstellt_at).toLocaleDateString("de-DE")} · letzte {bericht.zeitraum_tage} Tage{bericht.modell ? "" : " · ohne Modell"}
          </div>
          <div style={{ whiteSpace: "pre-wrap" }}>{bericht.zusammenfassung}</div>
        </div>
      )}

      {imBacklog.length > 0 && (
        <div className="pp-card divide-y mb-3">
          {imBacklog.map((v) => (
            <div key={v.id} className="px-4 py-2.5 text-[13px]">
              <span className="font-medium">{v.titel}</span>{" "}
              {v.issue_url ? <a className="underline" href={v.issue_url} target="_blank" rel="noreferrer">Ticket #{v.issue_nummer}</a> : null}
              <div className="text-[12.5px] text-muted-foreground">
                {v.wirkung?.vorher
                  ? `Anteil dieses Grunds vorher ${v.wirkung.vorher.quote ?? "—"} % (${v.wirkung.vorher.gesamt} Anrufe) → nachher ${v.wirkung.nachher?.quote ?? "—"} % (${v.wirkung.nachher?.gesamt ?? 0} Anrufe)`
                    + (v.wirkung.besser === true ? " · wirkt" : v.wirkung.besser === false ? " · keine Besserung" : " · noch zu wenige Anrufe danach")
                  : "Wirkung wird gemessen, sobald das Ticket geschlossen ist."}
              </div>
            </div>
          ))}
        </div>
      )}

      {faelle.length > 0 && (
        <details className="text-[12.5px] mb-2">
          <summary className="cursor-pointer text-muted-foreground">Die einzelnen Anrufe mit Nacharbeit ({faelle.length})</summary>
          <div className="pp-card divide-y mt-1.5">
            {faelle.map((f) => (
              <div key={f.id} className="px-4 py-2.5">
                <div><strong>{tag(f.started_at)}</strong> · {f.tenant_name}{f.anrufer_name ? ` · ${f.anrufer_name}` : ""} — {f.headline || "Anruf"}</div>
                {f.flag_reason ? <div className="text-muted-foreground">Warnung des Bots: {f.flag_reason.split("\n")[0]}</div> : null}
                <ul className="ml-4 list-disc text-muted-foreground">{f.spuren.map((s, i) => <li key={i}>{s.bezeichnung}</li>)}</ul>
                {f.deutung
                  ? <div className="mt-0.5"><strong>{f.deutung.grund_text}</strong>{f.deutung.vermeidbar ? " · vermeidbar" : ""} — {f.deutung.begruendung}
                      {f.deutung.verbesserung ? <div className="text-muted-foreground">→ {f.deutung.verbesserung}</div> : null}</div>
                  : <div className="text-muted-foreground">{f.stand === "final" ? "Deutung steht aus (läuft einmal am Tag)." : "Fenster von 72 Stunden läuft noch."}</div>}
              </div>
            ))}
          </div>
        </details>
      )}
    </section>
  )
}
